<template>
    <form-wrapper>
        <div class="reservation-filter__filter-button">
            <span class="reservation-filter__filter-button--span" @click="toogleFilter">
                <common-icon name="filter" size="huge"></common-icon>
                {{ $t('forms.offer.offer_filter.filter') }}
                <div v-if="numberOfActiveFilters" class="reservation-filter__filter-button--span__background"></div>
                <div v-if="numberOfActiveFilters" class="reservation-filter__filter-button--span__amount">
                    {{ numberOfActiveFilters }}
                </div>
            </span>
            <span @click="togleSortDropdown">
                <div class="offer-filter__sort--btn offer-filter__sort--btn-sort">
                    <common-icon name="sort" size="large"></common-icon>
                    {{ defaultSortName }}
                </div>
                <common-dropdown
                    v-click-away="closeSortDropdown"
                    class="offer-filter__sort--dropdown"
                    :isOpen="isSortDropdownOpen"
                    :recalculatePosition="true"
                    :hasArrow="false"
                    arrowPositionX="0"
                    arrowPositionY="0"
                >
                    <nav class="dropdown__menu">
                        <button class="form-input-select__options--none" @click="clearOrderBy({ path: 'orderBy' })">
                            {{ $t('common.microcopy.none') }}
                        </button>
                        <button v-for="sortOpt in sortOptions" :key="sortOpt" @click="sortClick(sortOpt)">
                            {{ $t(`${sortOpt.label}`) }}
                        </button>
                    </nav>
                </common-dropdown>
            </span>
        </div>
        <form-row v-if="showFilter" class="reservation-filter__row">
            <form-cell :width="getCellWidthAnd">
                <text-input
                    :formData="data"
                    :label="$t('forms.offer.offer_filter.fields.term.label')"
                    :placeholder="$t('forms.offer.offer_filter.fields.term.placeholder')"
                    valuePath="resFilter"
                    @change="searchChange"
                    @keypress.enter.prevent
                ></text-input>
            </form-cell>
            <form-cell :width="getCellWidthAnd">
                <multi-select-input
                    :formData="data"
                    valuePath="status"
                    :label="$t('forms.reservations.reservation_filter.fields.status.label')"
                    :placeholder="$t('forms.reservations.reservation_filter.fields.status.placeholder')"
                    :options="statusOptions"
                    :showSelectAllOption="true"
                    @change="changeWithSubmit"
                ></multi-select-input>
            </form-cell>
            <form-cell v-if="showUserFilter" width="third">
                <multi-select-input
                    class="my-calendar__filter--select"
                    :label="$t('pages.calendar.my_calendar.offer_user')"
                    :placeholder="$t('common.microcopy.select')"
                    :options="userOfferOptions"
                    :value="selectedUserOfferOptions"
                    :showSelectAllOption="true"
                    @change="changeUserOptions"
                ></multi-select-input>
            </form-cell>
            <form-cell :width="getCellWidthOr">
                <datepicker-input
                    ref="resDateFrom"
                    :formData="data"
                    :label="$t('forms.reservations.reservation_filter.fields.date_from.label')"
                    valuePath="resDateFrom"
                    :placeholder="$t('forms.reservations.reservation_filter.fields.date_from.placeholder')"
                    :upperLimit="upperLimit"
                    :prefillValue="dateFromInput"
                    @date-changed="dateFromChanged"
                    @keypress.enter.prevent
                ></datepicker-input>
            </form-cell>
            <form-cell :width="getCellWidthOr">
                <datepicker-input
                    ref="resDateTo"
                    :formData="data"
                    :label="$t('forms.reservations.reservation_filter.fields.date_to.label')"
                    :placeholder="$t('forms.reservations.reservation_filter.fields.date_to.placeholder')"
                    valuePath="resDateTo"
                    :lowerLimit="lowerLimit"
                    :prefillValue="dateToInput"
                    @date-changed="dateToChanged"
                    @keypress.enter.prevent
                ></datepicker-input>
            </form-cell>
            <form-cell v-if="showCompanyFilter" width="third">
                <multi-select-input
                    class="my-calendar__filter--select"
                    :label="$t('pages.calendar.my_calendar.offer_company')"
                    :placeholder="$t('common.microcopy.select')"
                    :options="companyOfferOptions"
                    :value="selectedCompanyOfferOptions"
                    :showSelectAllOption="true"
                    :hasExpandableOptions="true"
                    @change="changeCompanyOptions"
                ></multi-select-input>
            </form-cell>
        </form-row>
        <form-row v-if="$auth.isUserAdmin() && showFilter">
            <form-cell width="half">
                <select-input
                    ref="companySelect"
                    :formData="data"
                    :label="$t('forms.reservations.reservation_filter.fields.company.label')"
                    :placeholder="$t('forms.reservations.reservation_filter.fields.company.placeholder')"
                    :isRequired="true"
                    :options="companies"
                    valuePath="companyId"
                    :isSearchable="true"
                    :isNullable="true"
                    @change="companyChanged"
                    @clear="clearCompany"
                ></select-input>
            </form-cell>
            <form-cell width="half">
                <text-input
                    :formData="data"
                    :label="$t('forms.reservations.reservation_filter.fields.user.label')"
                    :placeholder="$t('forms.reservations.reservation_filter.fields.user.placeholder')"
                    :isRequired="true"
                    type="number"
                    valuePath="userId"
                    @change="userIdChange"
                    @keypress.enter.prevent
                ></text-input>
            </form-cell>
        </form-row>
    </form-wrapper>
</template>

<script>
    import ReservationStatusFilterEnum from 'enums/reservation_status_filter.enum'
    import ReservationSortFilterEnum from 'enums/reservation_sort_filter.enum'
    import RoutesEnum from 'enums/routes.enum'
    import ReservationTypeEnum from 'enums/reservation_type.enum'
    import FormMixin from 'mixins/FormMixin'
    import debounce from 'lodash.debounce'
    import OfferAPI from 'api/OfferAPI'
    import cloneDeep from 'lodash.clonedeep'
    import WindowResizeMixin from 'mixins/WindowResizeMixin'
    import { dateFormat } from '~/helpers'
    import CompanyAPI from 'api/CompanyAPI'

    export default {
        mixins: [FormMixin, WindowResizeMixin],

        props: {
            prefill: { type: Object, default: undefined },
            showFilterList: { type: Array, default: () => [] },
        },

        data() {
            return {
                upperLimit: null,
                lowerLimit: null,
                MIN_LENGTH: 3,
                showExcludeOffers: this.$route.params.type === ReservationTypeEnum.RECEIVED,
                selectedOffers: [],
                selectedUserOfferOptions: [],
                selectedCompanyOfferOptions: [],
                userOfferOptions: [],
                allUserOfferIds: [],
                allCompanyOfferIds: [],
                companyOfferOptions: [],
                excludeOfferIds: [],
                showFilter: false,
                sortLabel: 'sort',
                isSortDropdownOpen: false,
                dateFromInput: undefined,
                dateToInput: undefined,
                appliedFilters: [],
                companies: [],
            }
        },

        computed: {
            allOfferOptionIds() {
                return [...this.allUserOfferIds, ...this.allCompanyOfferIds]
            },

            showCompanyFilter() {
                return this.showExcludeOffers && this.companyOfferOptions?.length
            },

            showUserFilter() {
                return this.showExcludeOffers && this.userOfferOptions?.length
            },

            getCellWidthOr() {
                return this.showCompanyFilter || this.showUserFilter ? 'third' : 'half'
            },

            getCellWidthAnd() {
                return this.showCompanyFilter && this.showUserFilter ? 'third' : 'half'
            },

            defaultSortName() {
                return this.$t(`forms.reservations.reservation_filter.sort.${this.sortLabel}`)
            },

            numberOfActiveFilters() {
                return this.appliedFilters.reduce((acc, curr) => {
                    acc += curr.quantity
                    return acc
                }, 0)
            },

            sortOptions() {
                return Object.values(ReservationSortFilterEnum).map((status) => ({
                    label: this.$t(
                        `forms.reservations.reservation_filter.sort.${status.value.orderBy}_${status.value.wayDefinition}`,
                    ),
                    status,
                    value: status.value,
                }))
            },

            statusOptions() {
                return Object.values(ReservationStatusFilterEnum)
                    .filter((item) => item.value !== ReservationStatusFilterEnum.UNAVAILABLE.value)
                    .map((status) => ({
                        label: this.$t(`components.reservations.reservation_status.statuses.${status.label}`),
                        status,
                        value: status.value,
                    }))
            },
        },

        methods: {
            searchChange: debounce(function ({ path, value }) {
                this.change({ path, value })
                if (!value) {
                    this.submit()
                }
                if (value?.length < this.MIN_LENGTH) {
                    return
                }
                this.submit()
            }, 500),

            userIdChange({ path, value }) {
                this.change({ path, value })
                this.submit()
            },

            clearCompany() {
                this.data.companyId = undefined
                this.submit()
            },

            companyChanged(event) {
                this.change(event)
                this.submit()
            },

            changeWithSubmit({ path, value }) {
                this.change({ path, value })
                this.submit()
            },
            dateFromChanged(value) {
                if (value !== undefined) {
                    this.lowerLimit = dateFormat(value)
                    this.setValue('resDateFrom', value)
                } else {
                    this.lowerLimit = null
                    delete this.data['resDateFrom']
                }
                this.submit()
            },
            dateToChanged(value) {
                if (value !== undefined) {
                    this.upperLimit = dateFormat(value)
                    this.setValue('resDateTo', value)
                } else {
                    this.upperLimit = null
                    delete this.data['resDateTo']
                }
                this.submit()
            },

            clearOrderBy(event) {
                this.clear(event)
                this.sortLabel = 'sort'

                this.data.orderBy = undefined
                this.submit()
            },

            getSortLabel() {
                const { orderBy, wayDefinition } = this.$route.query
                if (orderBy && wayDefinition) {
                    this.sortLabel = `${orderBy}_${wayDefinition}`
                } else {
                    this.sortLabel = `${ReservationSortFilterEnum.CREATED_AT_DESC.value.orderBy}_${ReservationSortFilterEnum.CREATED_AT_DESC.value.wayDefinition}`
                }
            },

            sortClick(sortOpt) {
                this.sortLabel = `${sortOpt.value.orderBy}_${sortOpt.value.wayDefinition}`
                this.data.orderBy = {}
                this.data.orderBy.orderBy = sortOpt.value.orderBy
                this.data.orderBy.wayDefinition = sortOpt.value.wayDefinition

                this.submit()
            },

            togleSortDropdown() {
                this.isSortDropdownOpen = !this.isSortDropdownOpen
                this.showFilter = false
            },

            closeSortDropdown() {
                this.isSortDropdownOpen = false
            },

            async submit() {
                const query = { ...this.$route.query }
                if (this.data.resDateFrom) {
                    query.resDateFrom = this.data.resDateFrom
                }
                if (this.data.resDateTo) {
                    query.resDateTo = this.data.resDateTo
                }
                if (this.data.status) {
                    query.status = this.data.status
                }
                if (this.data.excludeOfferIds) {
                    query.excludeOfferIds = this.excludeOfferIds
                }
                if (this.data?.resFilter) {
                    query.resFilter = this.data.resFilter
                } else {
                    query.resFilter = undefined
                }
                if (this.data.orderBy) {
                    query.orderBy = this.data.orderBy.orderBy
                    query.wayDefinition = this.data.orderBy.wayDefinition
                }
                if (this.data.userId) {
                    query.userId = this.data.userId
                }

                if (this.data.companyId) {
                    query.companyId = this.data.companyId
                }

                if (this.$auth.isSubdomainActive()) {
                    await this.routerPush(query, RoutesEnum.SUBDOMAIN.RESERVATIONS).then(() => {
                        this.emitEvent({ name: 'filter-change', payload: query })
                    })
                } else {
                    if (this.$auth.isUserAdmin()) {
                        await this.routerPush(query, RoutesEnum.ADMIN.RESERVATIONS).then(() => {
                            this.emitEvent({ name: 'filter-change', payload: query })
                        })
                    } else {
                        await this.routerPush(query, RoutesEnum.RESERVATIONS.MY).then(() => {
                            this.emitEvent({ name: 'filter-change', payload: query })
                        })
                    }
                }
            },

            async routerPush(query, route) {
                return this.$router.push({ name: route, query })
            },

            loadMyOffers() {
                OfferAPI.getMy().then((offers) => {
                    this.userOfferOptions = offers.personalOffers.map((offer) => ({
                        label: offer.titleTextContent.originalText,
                        value: offer.id,
                    }))
                    this.selectedUserOfferOptions = this.userOfferOptions.map((option) => option.value)
                    this.allUserOfferIds = cloneDeep(this.selectedUserOfferOptions)

                    this.companyOfferOptions = offers.companies.map((company) => ({
                        heading: company.name,
                        value: company.id,
                        options: company.offers.map((offer) => ({
                            label: offer.titleTextContent.originalText,
                            value: offer.id,
                        })),
                    }))
                    this.selectedCompanyOfferOptions = this.companyOfferOptions.reduce((acc, companies) => {
                        return [...acc, ...companies.options.map((option) => option.value)]
                    }, [])
                    this.allCompanyOfferIds = cloneDeep(this.selectedCompanyOfferOptions)

                    if (this.prefill.excludeOfferIds.length) {
                        this.selectedUserOfferOptions = this.selectedUserOfferOptions.filter(
                            (el) => !this.prefill.excludeOfferIds.includes(el),
                        )
                        this.selectedCompanyOfferOptions = this.selectedCompanyOfferOptions.filter(
                            (el) => !this.prefill.excludeOfferIds.includes(el),
                        )
                    }

                    this.getNumberOfFiltersApplied()
                })
            },
            changeUserOptions({ path, value }) {
                this.selectedUserOfferOptions = value
                this.change({ path, value })
                this.changeExcludedOfferIds()
            },

            changeCompanyOptions({ path, value }) {
                this.selectedCompanyOfferOptions = value
                this.change({ path, value })
                this.changeExcludedOfferIds()
            },

            changeExcludedOfferIds() {
                this.excludeOfferIds = this.allOfferOptionIds.filter(
                    (el) => !this.selectedUserOfferOptions.includes(el) && !this.selectedCompanyOfferOptions.includes(el),
                )
                this.data.excludeOfferIds = this.excludeOfferIds
                this.submit()
            },

            toogleFilter() {
                this.showFilter = !this.showFilter
                this.isSortDropdownOpen = false
            },

            removeFilters() {
                delete this.data.status
                delete this.data.resDilter
                delete this.data['resDateFrom']
                delete this.data['resDateTo']
                this.dateFromInput = null
                this.dateToInput = null
            },

            addToFilters(name, quantity) {
                const isFilterApplied = this.appliedFilters.find((f) => f.name == name)
                !isFilterApplied
                    ? this.appliedFilters.push({ name: name, quantity: quantity })
                    : (isFilterApplied.quantity = quantity)
            },

            getNumberOfFiltersApplied() {
                this.appliedFilters = []
                if (this.prefill?.resFilter) {
                    this.addToFilters('resDilter', 1)
                }
                if (this.prefill?.resDateFrom) {
                    this.addToFilters('resDateFrom', 1)
                }
                if (this.prefill?.resDateTo) {
                    this.addToFilters('resDateTo', 1)
                }
                if (this.prefill?.status?.length) {
                    this.addToFilters('status', 1)
                }
                if (this.prefill.excludeOfferIds?.length && !isNaN(this.prefill.excludeOfferIds[0])) {
                    this.addToFilters('excludeOfferIds', this.prefill.excludeOfferIds.length)
                }
            },

            getCompanies() {
                CompanyAPI.getAllCompanies().then(
                    (response) =>
                        (this.companies = response.map((item) => ({
                            label: item.name,
                            value: item.id,
                        }))),
                )
            },
        },

        watch: {
            prefill() {
                this.getNumberOfFiltersApplied()
                this.prefillData(this.prefill)
                if (this.prefill.resDateFrom) {
                    this.dateFromInput = this.prefill.resDateFrom
                } else {
                    if (this.$refs.resDateFrom) {
                        this.$refs.resDateFrom.selected = undefined
                    }
                }
                if (this.prefill.resDateTo) {
                    this.dateToInput = this.prefill.resDateTo
                } else {
                    if (this.$refs.resDateTo) {
                        this.$refs.resDateTo.selected = undefined
                    }
                }
            },
            '$route.params.type': {
                handler: function () {
                    this.removeFilters()
                    this.showFilter = false
                    this.showExcludeOffers = this.$route.params.type === ReservationTypeEnum.RECEIVED
                    this.getSortLabel()
                    if (this.showExcludeOffers && !this.$auth.isUserAdmin()) {
                        this.loadMyOffers()
                    }
                },
            },
        },

        mounted() {
            if (this.$auth.isUserAdmin()) {
                this.getCompanies()
            }
            this.getNumberOfFiltersApplied()
            if (this.showExcludeOffers && !this.$auth.isUserAdmin()) {
                this.loadMyOffers()
            }
            this.getSortLabel()

            this.prefillData(this.prefill)
            if (this.prefill.resDateFrom) {
                this.dateFromInput = this.prefill.resDateFrom
            }
            if (this.prefill.resDateTo) {
                this.dateToInput = this.prefill.resDateTo
            }
        },
    }
</script>

<style lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/mixins';
    @import 'styles/components/offers/_offer-filter.scss';

    .reservation-filter__row {
        display: flex;
        flex-wrap: wrap;
    }

    .reservation-filter__filter-button {
        display: flex;
        justify-content: space-between;
        align-items: center;
        &--span {
            position: relative;
            display: flex;
            align-items: center;
            max-height: 0;
            min-width: 80px;
            color: $color--accent-1;
            font-weight: 500;
            font-size: $font-size--header-copy;
            &:hover {
                cursor: pointer;
                opacity: 0.7;
            }

            &__background {
                position: absolute;
                height: 18px;
                width: 18px;
                background: $color--accent-4;
                border-radius: 50%;
                left: 13px;
                top: -21px;
                z-index: $layer--above-parent;
                pointer-events: none;
            }

            &__amount {
                width: 18px;
                height: 18px;
                position: absolute;
                left: 13px;
                top: -20px;
                text-align: center;
                z-index: $layer--tooltip;
                color: $color-white;
                font-size: $font-size--medium;
                pointer-events: none;
            }
        }
    }

    .subdomain-layout {
        .reservation-filter__filter-button {
            &--span {
                color: $color-black;
            }
        }

        &--black {
            .reservation-filter__filter-button {
                &--span {
                    color: $color-white;
                }
            }
        }

        &--navy_black {
            .reservation-filter__filter-button {
                .reservation-filter__filter-button {
                    &--span {
                        color: $subdomain-blue;
                    }
                }
            }
        }
    }
</style>
